import { invalidateCookie } from '~/utils/cookies';
import { submitLogin, submitLogout } from '~/utils/auth';
import { defineStore, useCookie, useRuntimeConfig } from '#imports';

interface UserPayloadInterface {
  username: string;
  password: string;
  captchaToken: string;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
      // authentication for test site login only
      isSiteAuthenticated: false,
      isAuthInitialized: false,
      user: null,
      // authenticated for WP user login
      authenticated: false,
      loading: false,
  }),
  getters: {
    isAuthenticated(state) {
      return state.authenticated
    },
    // test site auth
    getAuthStatus: (state) => state.isSiteAuthenticated,
    getCurrentUser: (state) => state.user,
  },
  actions: {
    // login to test site
    async siteLogin(username, password) {
      const validCredentials = this.validateCredentials(username, password)

      if (validCredentials) {
        this.isSiteAuthenticated = true
        this.user = { username }

        // Set cookie with 7 days expiration
        const authCookie = useCookie('site-auth', {
          default: () => null,
          maxAge: 60 * 60 * 24 * 7,
          sameSite: 'strict',
          secure: true
        })

        authCookie.value = {
          isAuthenticated: true,
          user: { username },
          timestamp: Date.now()
        }

        return { success: true }
      } else {
        return { success: false, message: 'Invalid credentials' }
      }
    },
    siteLogout() {
      this.isSiteAuthenticated = false
      this.user = null

      // Clear the cookie
      const authCookie = useCookie('site-auth')
      authCookie.value = null
    },
    validateCredentials(username, password) {
      const config = useRuntimeConfig();

      const validUsers = [
        { username: config.public.testSiteUser, password: config.public.testSiteKey }
      ]

      return validUsers.some(user =>
        user.username === username && user.password === password
      )
    },
    initializeAuth() {
      const config = useRuntimeConfig();

      const authCookie = useCookie('site-auth', {
        default: () => null
      })

      // check flag from .env
      if (config.public.maintenanceMode !== 'true') {
        this.isSiteAuthenticated = true
        this.isAuthInitialized = true
        return
      }

      if (authCookie.value && this.isValidSession(authCookie.value)) {
        this.isSiteAuthenticated = authCookie.value.isAuthenticated
        this.user = authCookie.value.user
      } else {
        // Clear invalid/expired cookie
        authCookie.value = null
        this.isSiteAuthenticated = false
      }

      this.isAuthInitialized = true
    },
    isValidSession(cookieData) {
      const config = useRuntimeConfig();

      // Check if session is still valid (not expired, etc.)
      const maxAge = 60 * 60 * 24 * 7 * 1000 // 7 days in milliseconds
      const isExpired = Date.now() - cookieData.timestamp > maxAge
      const userMatch = cookieData.user.username == config.public.testSiteUser

      return !isExpired && cookieData.isAuthenticated && userMatch
    },

    setAuthLoading(state) {
      this.loading = state
    },
    async authenticateUser({ username, password, captchaToken }: UserPayloadInterface) {
      this.loading = true

      const result = await submitLogin({ login: username, password, captchaToken })
      this.loading = false

      if (! result || result.errors) {
        this.invalidateSession();
        return undefined;
      }

      this.validateSession();

      return result;
    },
    validateSession() {
      this.authenticated = true
    },
    async logoutUser() {
      this.invalidateSession()
      await submitLogout()
    },
    invalidateSession() {
      invalidateCookie('woo-session')

      this.authenticated = false
    }
  },
})
